/**
 * The storefront a visitor asked for by hostname.
 *
 * On `my-store.vntru.com` the whole site *is* that store: its pages
 * answer at the root (`/`, `/products/42`) instead of under `/store/{id}`,
 * which stays the owner-facing design studio on the platform host.
 *
 * Resolved once per request by the `storefront-host` plugin and carried to the
 * client in the payload, so the pages can read it synchronously.
 */
export interface StorefrontHost {
  /** Store label in the host, or `null` on the platform itself. */
  subdomain: string | null
  /** Resolved store, once the subdomain has been traded for one. */
  storeId: string | null
  /** The host names a real store whose owner has not published it yet. */
  unpublished: boolean
  /** No store answers to this host. */
  missing: boolean
  /**
   * SSR said this host was missing. The client re-checks before the page
   * may paint "not found" — a guest subdomain lookup can 404 a shop that
   * the browser then loads (publish race, `{shop}.localhost` DNS).
   */
  verifying: boolean
  /** Public storefront reads were rejected with HTTP 429. */
  rateLimited: boolean
}

const EMPTY: StorefrontHost = {
  subdomain: null,
  storeId: null,
  unpublished: false,
  missing: false,
  verifying: false,
  rateLimited: false,
}

export function useStorefrontHost() {
  return useState<StorefrontHost>('storefront-host', () => ({ ...EMPTY }))
}

/** Shared flag so a 429 on `/store/{id}` is not mistaken for "not found". */
export function useStorefrontRateLimited() {
  return useState<boolean>('storefront-rate-limited', () => false)
}

export function isStorefrontRateLimited(host?: StorefrontHost): boolean {
  return Boolean(host?.rateLimited)
}

export function markStorefrontRateLimited() {
  useStorefrontRateLimited().value = true
  const host = useStorefrontHost()
  host.value = { ...host.value, rateLimited: true, missing: false }
}

/**
 * Guest-facing "this storefront is gone": unknown host, inactive, or domain
 * revoked (the old label no longer maps to any live store).
 *
 * Distinct from {@link StorefrontHost.unpublished}, which still names a real
 * store the owner has not published yet, and from a rate-limit (429).
 */
/** SSR "missing" is still being re-checked on the client. */
export function isStorefrontHostVerifying(host?: StorefrontHost): boolean {
  return Boolean(host?.verifying)
}

/**
 * Subdomain is known but we do not yet have a store id, a confirmed miss,
 * unpublished, or rate-limit. Paint loading — never "not found" — until one
 * of those settles (publish race / SSR transport miss).
 */
export function isStorefrontHostPending(host?: StorefrontHost): boolean {
  if (!host?.subdomain) return false
  if (host.verifying) return true
  return !host.storeId
    && !host.unpublished
    && !host.rateLimited
    && !host.missing
}

export function isStorefrontMissing(host: StorefrontHost): boolean {
  if (host.rateLimited || host.verifying || isStorefrontHostPending(host)) return false
  return host.missing
}

/** Placeholder hosts parked after an admin domain revocation. */
export function isRevokedStorefrontLabel(subdomain: string | null | undefined): boolean {
  return Boolean(subdomain && subdomain.startsWith('__revoked_'))
}

/** Store label in the host being served right now, if any. */
export function currentStoreSubdomain(): string | null {
  const { public: config } = useRuntimeConfig()

  return storeSubdomainFrom(
    useRequestURL().host,
    String(config.storeRootDomain || ''),
  )
}

/**
 * Store the current page belongs to.
 *
 * Storefront pages are reachable two ways — `/store/{id}` on the platform host
 * and the bare path on the store's own host — so the id comes from whichever
 * of the two named it.
 */
export function useStorefrontStoreId(): ComputedRef<string> {
  const route = useRoute()
  const host = useStorefrontHost()

  return computed(() => {
    const fromPath = route.params.id

    return String(
      (Array.isArray(fromPath) ? fromPath[0] : fromPath) || host.value.storeId || '',
    )
  })
}

/**
 * Path to a storefront page, addressed the way the current host expects.
 *
 * On the store's own host these stay clean (`/products/42`); everywhere else
 * they keep the `/store/{id}` prefix the design studio is served from.
 *
 * Pure so callers can resolve {@link useStorefrontHost} up front, in setup,
 * and still build paths later while rendering.
 */
export function storefrontPath(
  host: StorefrontHost,
  storeId: string,
  suffix = '',
): string {
  return host.subdomain && host.storeId === storeId
    ? (suffix || '/')
    : `/store/${encodeURIComponent(storeId)}${suffix}`
}

/**
 * Store a storefront path belongs to, or `null` off the storefront.
 *
 * The mirror of {@link storefrontPath}: on the store's own host every path is
 * part of that storefront (`/`, `/pages/about`), while the platform host only
 * serves one under `/store/{id}`. Pure, so plugins can call it per navigation.
 */
export function storefrontStoreIdForPath(
  host: StorefrontHost,
  path: string,
): string | null {
  const match = path.match(/^\/store\/([^/]+)/)
  if (match?.[1]) return decodeURIComponent(match[1])
  return host.subdomain && host.storeId ? host.storeId : null
}

/** `/store/{id}` on the platform host, or any path on a store's own host. */
export function isStorefrontRequest(path: string): boolean {
  if (path.startsWith('/store')) return true
  return Boolean(currentStoreSubdomain())
}

export function useStorefrontPaths(storeId: MaybeRefOrGetter<string>) {
  const host = useStorefrontHost()

  function path(suffix = ''): string {
    return storefrontPath(host.value, String(toValue(storeId) || ''), suffix)
  }

  return {
    home: computed(() => path()),
    checkout: computed(() => path('/checkout')),
    order: (orderId: string | number) =>
      path(`/checkout/${encodeURIComponent(String(orderId))}`),
    publish: computed(() => path('/publish')),
    product: (productId: string | number) =>
      path(`/products/${encodeURIComponent(String(productId))}`),
    collection: (collectionId: string | number) =>
      path(`/collections/${encodeURIComponent(String(collectionId))}`),
  }
}
